<template>
  <div class="app-container">
    <div v-if="!hasPermission" class="no-permission">
      <i class="el-icon-warning-outline"></i>
      <h2>您没有权限访问此页面</h2>
      <p>请联系系统管理员获取权限</p>
    </div>

    <template v-else>
      <!-- 搜索区域 -->
      <el-card class="search-card" shadow="never">
        <el-form
          :model="filters"
          ref="queryForm"
          size="small"
          :inline="true"
          label-width="80px"
          @submit.native.prevent
        >
          <el-form-item label="用户名" prop="username">
            <el-input
              v-model="filters.username"
              placeholder="请输入招商方用户名"
              clearable
              style="width: 240px;"
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" size="mini" @click="handleSearch">搜索</el-button>
            <el-button icon="el-icon-refresh" size="mini" @click="handleReset">重置</el-button>
          </el-form-item>
        </el-form>
      </el-card>

      <!-- 列表区域 -->
      <el-card class="table-card" shadow="never">
        <div class="table-toolbar">
          <div class="toolbar-left">
            <el-button
              type="primary"
              plain
              icon="el-icon-plus"
              size="mini"
              @click="openCreateDialog"
            >新增招商账号</el-button>
          </div>
          <div class="toolbar-right">
            <el-tooltip content="刷新" placement="top">
              <el-button
                circle
                icon="el-icon-refresh"
                size="mini"
                @click="fetchList"
              />
            </el-tooltip>
          </div>
        </div>

        <el-table
          v-loading="loading"
          :data="pagedList"
          border
          stripe
          size="small"
          style="width: 100%;"
          :header-cell-style="headerCellStyle"
        >
          <el-table-column type="index" label="序号" width="60" align="center" :index="indexMethod" />
          <el-table-column prop="username" label="用户名" min-width="180" show-overflow-tooltip />
          <el-table-column label="角色" width="120" align="center">
            <template slot-scope="scope">
              <el-tag size="mini" type="success">{{ getRoleLabel(scope.row) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="100" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button
                type="text"
                icon="el-icon-delete"
                size="mini"
                class="danger-text"
                :disabled="scope.row.id == null"
                @click="handleDelete(scope.row)"
              >删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination-wrap">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next, jumper"
            :total="filteredList.length"
            :current-page.sync="pageNum"
            :page-size.sync="pageSize"
            :page-sizes="[10, 20, 50]"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
          />
        </div>
      </el-card>

      <!-- 新增招商账号弹窗 -->
      <el-dialog
        title="新增招商账号"
        :visible.sync="dialogVisible"
        width="500px"
        append-to-body
        :close-on-click-modal="false"
        @closed="resetForm"
      >
        <el-form
          :model="form"
          :rules="rules"
          ref="accountForm"
          label-width="100px"
          size="small"
        >
          <el-form-item label="用户名" prop="username">
            <el-input v-model="form.username" placeholder="请输入用户名" maxlength="20" />
          </el-form-item>
          <el-form-item label="密码" prop="password">
            <el-input
              v-model="form.password"
              type="password"
              placeholder="请输入密码"
              show-password
            />
          </el-form-item>
          <el-form-item label="确认密码" prop="confirmPassword">
            <el-input
              v-model="form.confirmPassword"
              type="password"
              placeholder="请再次输入密码"
              show-password
            />
          </el-form-item>
          <el-form-item label="角色">
            <el-tag size="small" type="success">招商方</el-tag>
            <span class="role-hint">新账号将自动创建为招商方</span>
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button type="primary" size="small" :loading="submitting" @click="handleSubmit">确 定</el-button>
          <el-button size="small" @click="dialogVisible = false">取 消</el-button>
        </div>
      </el-dialog>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import request from '@/utils/request'
import { PERMISSION, getPermissionLabel } from '@/utils/enums'

export default {
  name: 'AgentAccountManagement',
  data() {
    const validateConfirmPassword = (rule, value, callback) => {
      if (value !== this.form.password) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }

    return {
      loading: false,
      submitting: false,
      accountList: [],
      filters: { username: '' },
      pageNum: 1,
      pageSize: 10,
      dialogVisible: false,
      form: {
        username: '',
        password: '',
        confirmPassword: ''
      },
      rules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 3, max: 20, message: '用户名长度在 3 到 20 个字符', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, max: 20, message: '密码长度在 6 到 20 个字符', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请再次输入密码', trigger: 'blur' },
          { validator: validateConfirmPassword, trigger: 'blur' }
        ]
      },
      headerCellStyle: {
        background: '#f8f8f9',
        color: '#515a6e',
        fontWeight: '600'
      }
    }
  },
  computed: {
    ...mapGetters({
      permission: 'user/permission'
    }),
    hasPermission() {
      try {
        const localStoragePermission = localStorage.getItem('user-permission')
        return localStoragePermission === '1' || this.permission === '1' || this.permission === 1
      } catch (error) {
        return false
      }
    },
    filteredList() {
      const keyword = (this.filters.username || '').trim().toLowerCase()
      if (!keyword) return this.accountList
      return this.accountList.filter(item =>
        (item.username || '').toLowerCase().includes(keyword)
      )
    },
    pagedList() {
      const start = (this.pageNum - 1) * this.pageSize
      return this.filteredList.slice(start, start + this.pageSize)
    }
  },
  created() {
    if (this.hasPermission) {
      this.fetchList()
    }
  },
  methods: {
    indexMethod(index) {
      return (this.pageNum - 1) * this.pageSize + index + 1
    },
    normalizeList(response) {
      let list = []
      if (Array.isArray(response)) {
        list = response
      } else if (response && Array.isArray(response.records)) {
        list = response.records
      } else if (response && Array.isArray(response.data)) {
        list = response.data
      }
      return list.map(item => {
        if (typeof item === 'string') {
          return { username: item, permission: PERMISSION.AGENT }
        }
        return {
          ...item,
          permission: item.permission != null ? item.permission : PERMISSION.AGENT
        }
      })
    },
    getPermissionValue(row) {
      return row.permission != null ? row.permission : PERMISSION.AGENT
    },
    getRoleLabel(row) {
      return getPermissionLabel(this.getPermissionValue(row))
    },
    async fetchList() {
      this.loading = true
      try {
        const data = await request.get('/api/account/list')
        this.accountList = this.normalizeList(data)
      } catch (error) {
        this.$message.error((error && error.message) || '获取招商账号列表失败')
        this.accountList = []
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pageNum = 1
    },
    handleReset() {
      this.filters.username = ''
      this.pageNum = 1
      this.fetchList()
    },
    handleSizeChange() {
      this.pageNum = 1
    },
    handlePageChange() {},
    openCreateDialog() {
      this.resetForm()
      this.dialogVisible = true
    },
    resetForm() {
      this.form = {
        username: '',
        password: '',
        confirmPassword: ''
      }
      this.$nextTick(() => {
        if (this.$refs.accountForm) this.$refs.accountForm.clearValidate()
      })
    },
    handleSubmit() {
      this.$refs.accountForm.validate(async (valid) => {
        if (!valid) return
        this.submitting = true
        try {
          await request.post('/api/account', {
            username: this.form.username.trim(),
            password: this.form.password
          })
          this.$message.success('招商账号创建成功')
          this.dialogVisible = false
          this.fetchList()
        } catch (error) {
          this.$message.error((error && error.message) || '创建失败，请重试')
        } finally {
          this.submitting = false
        }
      })
    },
    handleDelete(row) {
      if (row.id == null) {
        this.$message.error('无法删除：缺少账号ID，请重启后端后刷新列表')
        return
      }
      this.$confirm(`是否确认删除招商账号「${row.username}」？`, '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await request.delete(`/api/account/${row.id}`)
          this.$message.success('删除成功')
          if (this.pagedList.length === 1 && this.pageNum > 1) {
            this.pageNum -= 1
          }
          this.fetchList()
        } catch (error) {
          this.$message.error((error && error.message) || '删除失败，请重试')
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.app-container {
  width: 100%;
}

.no-permission {
  background: #fff;
  border-radius: 4px;
  padding: 60px 24px;
  text-align: center;
  color: #909399;
}

.no-permission i {
  font-size: 48px;
  color: #e6a23c;
  margin-bottom: 12px;
}

.no-permission h2 {
  margin: 8px 0;
  font-size: 18px;
  color: #303133;
  font-weight: 500;
}

.search-card {
  margin-bottom: 10px;
}

.search-card >>> .el-card__body {
  padding: 18px 18px 0;
}

.table-card >>> .el-card__body {
  padding: 16px;
}

.table-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-wrap {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.dialog-footer {
  text-align: right;
}

.role-hint {
  margin-left: 10px;
  color: #909399;
  font-size: 12px;
}

.danger-text {
  color: #f56c6c !important;
}
</style>
